import React, {useEffect, useMemo} from "react";
import {Label, Offcanvas, OffcanvasBody, OffcanvasHeader} from "reactstrap";
import NoSearchResult from "../../../../components/Common/NoSearchResult";
import {useFormik} from 'formik';
import * as yup from 'yup';
import {useDispatch, useSelector} from "react-redux";
import {IOffice} from "../../../../store/offices/types";
import {clearOffices, getOffices} from "../../../../store/offices/actions";
import {ICommonOrderCreateForm} from "../../../../store/commonOrders/types";
import LabelInput from "../../../../components/Common/LabelInput";
import Select from "../../../../components/Common/Select";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import {CommonOrderService} from "../../../../services/common_order.service";
import moment from "moment";
import MultiDatePicker from "../../../../components/Common/MultiDatePicker";

interface ICreateCommonOrder {
    isOpen: boolean,
    toggle: () => void,
}

const CreateCommonOrder = ({isOpen, toggle}: ICreateCommonOrder) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const loadingOffices = useSelector((state: any) => state.Offices.loading as boolean);
    const offices = useSelector((state: any) => (state.Offices.data || []) as Array<IOffice>);

    const {
        data: trafficOrders,
        isLoading: loadingOrders,
    } = useQuery({
        queryKey: ["keyTrafficOrders"],
        queryFn: () => TrafficOrderService.getTrafficOrders(),
        retry: false,
    });

    const {
        mutate: createCommonOrder,
        isSuccess: isSuccessCreate,
        isPending: isPendingCreate,
    } = useMutation({
        mutationFn: CommonOrderService.createCommonOrder,
        mutationKey: ['keyCreateCommonOrder']
    });

    useEffect(() => {
        dispatch(getOffices('', {}))

        return () => {
            dispatch(clearOffices())
        };
    }, []);

    useEffect(() => {
        if (isSuccessCreate) {
            toggle()
            queryClient.invalidateQueries({queryKey: ['keyCommonOrders']})
        }
    }, [isSuccessCreate]);

    const handleDatesChange = (newDates: Date[]) => {
        const sortedDates = [...newDates]
            .map(date => moment(date).format("YYYY-MM-DD"))
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        setFieldValue("dates", sortedDates);
    };

    const validationSchema: yup.Schema<ICommonOrderCreateForm> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .max(75, "max 75 characters")
            .required("required"),
        order: yup.object({
            order_id: yup.number().required("required"),
            office_id: yup.number().required("required"),
        }).required("required"),
        office_ids: yup.array()
            .min(1, "min 1 item")
            .required("required"),
        dates: yup.array()
            .min(1, "min 1 item")
            .required("required")
    });

    const initialValues: ICommonOrderCreateForm = {
        name: "",
        order: null,
        office_ids: [],
        dates: []
    }

    const handleSubmitForm = (value: ICommonOrderCreateForm) => {
        const missingOrders = value.office_ids
            .filter(id => value.order?.office_id !== id)
            .map(id => ({order_id: null, office_id: id}));
        const orders = [value.order, ...missingOrders];

        const request = {name: values.name, data: orders, dates: value.dates}
        createCommonOrder(request);
    }

    const {
        values,
        handleSubmit,
        handleChange,
        handleBlur,
        errors,
        touched,
        isValid,
        setFieldValue
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const ordersSelect = useMemo(() => (
        trafficOrders?.data.map((item: any) => ({
            value: item.id,
            label: `[${item.id}] | ${item.broker.name}`,
            office_id: item.office_id
        }))
    ), [trafficOrders?.data]);

    const officesSelect = useMemo(() => (
        offices.map((item: any) => ({
            value: item.id,
            label: item.name
        }))
    ), [offices]);

    const handleOrderSelect = (order: any) => {
        if (order) {
            setFieldValue("order", {order_id: order.value, office_id: order.office_id});
            setFieldValue("office_ids", [order.office_id]);
        } else {
            setFieldValue("order", null);
            setFieldValue("office_ids", []);
        }
    }

    const handleOfficesSelect = (value: any) => {
        const ids: number[] = value.map((item: any) => item.value)
        if (values.order && !ids.includes(values.order.office_id)) {
            handleOrderSelect(null)
        } else
            setFieldValue("office_ids", ids)
    }

    const orderSelectValue = values.order ? ordersSelect.find((order: any) => values.order?.order_id === order.value) : null

    return (
        <Offcanvas isOpen={isOpen} direction="end" toggle={toggle}>
            <OffcanvasHeader toggle={toggle}> Add Common Order </OffcanvasHeader>

            {!loadingOffices && !loadingOrders ? (
                <OffcanvasBody>
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <LabelInput
                                label="Name"
                                placeholder="Name"
                                name="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                hasError={!!(errors.name && touched.name)}
                                errorText={errors.name}
                            />
                        </div>

                        <div className="mb-3">
                            <label>Select date</label>
                            <MultiDatePicker
                                onDatesChange={(newDates: Date[]) => handleDatesChange(newDates)}
                            />
                        </div>

                        <div className='mb-3'>
                            <Label>Order: </Label>
                            <Select
                                isSearchable
                                name="order_id"
                                label="Order"
                                placeholder="Order"
                                options={ordersSelect}
                                value={orderSelectValue}
                                onChange={(order: any) => handleOrderSelect(order)}
                            />
                        </div>

                        <div className='mb-3'>
                            <Label>Response Offices: </Label>
                            <Select
                                isMulti
                                isSearchable
                                name="office_id"
                                label="Response Office"
                                placeholder="Response Office"
                                options={officesSelect}
                                value={values.office_ids.map(o => officesSelect.find(os => os.value === o))}
                                onChange={(value: any) => handleOfficesSelect(value)}
                                disabled={!values.order}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary w-100" disabled={!isValid || isPendingCreate}>
                            {isPendingCreate ? "Loading..." : "Save"}
                        </button>
                    </form>
                </OffcanvasBody>
            ) : (
                <NoSearchResult text="Loading ..."/>
            )}
        </Offcanvas>
    );
};

export default CreateCommonOrder;